// eslint-disable-next-line
import {injectable} from "inversify";
import {RESOLUTION_CATEGORY} from "../../models/ResolutionTypes";
import {ICreateRmaDefaultSettings, ICRMAFeeDto} from "../../models/User";
import {IAutoRefundDto} from "../../pages/create-rma/stores/rma-auto-refund-store";
import {ICreateRmaDto} from "../../pages/create-rma/stores/rma-request-store";
import {RETURN_METHOD_TYPE} from "../../pages/return-methods/return-method-types";
import {CREDIT_AMOUNT_TYPE, RESOLUTION_TYPE} from "../../pages/resolutions/constants";
import { IApiResponse, IHttpService } from "./../http-service";
import { TYPE, rootContainer } from "./../container/root";
import { FEE_TYPE, IPolicyFee, IPolicyRule } from "../../pages/policies/policy-types";
import { CHARGE_FEES } from "../../pages/policies/constants";
import { IFollowUpQuestion } from "../../models/ReturnReasonTypes";

export interface IRmaRequestItems {
	line_item_id: string;
	quantity: number;
    return_method_id: number;
	resolution_id: number;
	reason_id: number;
	restocking_fee: number;
	adjustments: number;
}
export interface IRmaRequest {
	order_id: string;
	order_name: string;
	items: IRmaRequestItems[];
	return_fee: ICRMAFeeDto;
	label_fee: ICRMAFeeDto;
	auto_refund: IAutoRefundDto;
	auto_mark_as_done: boolean;
	auto_approve: boolean;
	note: string;
}

export interface ICRmaReason{
	id: number;
	internal_name: string;
    is_enabled: boolean;
    follow_up_questions?: IFollowUpQuestion[];
}
export interface ICRmaResolution {
	id: number;
	internal_name: string;
    resolution_type: RESOLUTION_TYPE;
    is_percentage: boolean;
    modify_credit_amount: number | undefined | null,
    credit_amount_type: CREDIT_AMOUNT_TYPE,
}
export interface IResolutionsCategoriesList {
	category: RESOLUTION_CATEGORY, 
    list: ICRmaResolution[]
}
export interface IReturnMethodsList{
	internal_name: string;
	id: number;
    type: RETURN_METHOD_TYPE;
	is_label_cost_supported: boolean;
}

export interface ICreateRMAResponse {
	created_rma?: {
		rma_code?: string;
	}
    success: boolean;
    message: string;
    message_title: string;
    warning_message?: string;
}

export interface ICreateRmaSettings {
    store_locations: IStoreLocationsMap;
    current_location: string;
	reasons_list: ICRmaReason[];
	resolutions_list: IResolutionsCategoriesList[];
	return_methods_list: IReturnMethodsList[];
    policy_rules: ICRmaPolicyRule[];
    allowed_modes: CREATE_RMA_MODE[];
    require_crma_followup_questions:REQUIRE_CRMA_FOLLOWUP_QUESTIONS;
}

export interface IFetchCreateRmaDefaultsRequest {
    order_name: string;
}

export interface IGetDynamicShippingOptionsResponse {
    is_dynamic_shipping_options_supported: boolean;
    shipping_options: IPortalDynamicShippingOptionDto[];
}

export interface IPortalDynamicShippingOptionDto {
    id: number;
    name: string;
    description: string;
    cost: number;
    currency_code: string;
}


export enum REQUIRE_CRMA_FOLLOWUP_QUESTIONS {
    DONT_SHOW = 'DONT_SHOW',
    MANDATORY = 'MANDATORY',
    OPTIONAL = 'OPTIONAL',
}

export interface ICRmaPolicyRule extends Partial<IPolicyRule> { 
    id: number;
    name: string;
    resolution_id: number; 
    return_method_id: number;
    charge_fees: CHARGE_FEES;
    merchant_resolution_setting: ICRmaPolicyResolutionSettings;
    policy_rule_fees: IPolicyFee[],
}

export interface ICRmaPolicyResolutionSettings {
    id: number;
    resolution_id: number;
}

export interface IStoreLocationsMap {
    [location_id: number]: IStoreLocation;
    
}

export interface IStoreLocation {
    name: string;
    location_id: string;
    fulfillsOnlineOrders: boolean;
    /** Can be extended on demand, since each location has more fields */
}

export interface IProductExchangeSearchResponse {
    products: IPlatformProduct[];
}

export interface IPlatformProduct {
    has_only_default_variant: boolean;
    options: [];
    id: number;
    title: string;
    image_src: string;
}

export interface IPlatformProductVariant {
    id: number;
    title: string;
    price: number;
    inventory_quantity: number;
}

export interface IShouldRequestManagersApproval {
    request_manager_approval: boolean,
    request_manager_approval_reason: string
}

export interface IOutOfPolicyResultDto {
    is_out_of_policy: boolean;
    return_items: {
        group_id: string;
        line_item_id: string;
        available_policies: number[];
    }[];
}

export interface ICreateRmaBalancePreviewResultDto {
    total_balance: number;
    is_create_rma_blocked: boolean;
}

export enum CREATE_RMA_MODE {
    C_RMA_GOD_MODE = 'god-mode',
    C_RMA_POLICY_LED_MODE = 'policy-led',
}

export function createRmaOptions() {
     return [
        {
            value: CREATE_RMA_MODE.C_RMA_GOD_MODE,
            labelTranslationKey: 'MODE_UNLIMITED',
        },
        {
            value: CREATE_RMA_MODE.C_RMA_POLICY_LED_MODE,
            labelTranslationKey: 'MODE_ENFORCED_POLICY_ELIGIBILITY',
        },
    ];
}


export interface ICRmaItemFeeDataRequest {
    policy_rule_id: number;
    paid_price: number;
    catalog_price: number;
    payment_currency: string;
    store_currency: string;
    is_order_covered: boolean;
}

export interface ICRmaItemFeeDataResponse {
    amount: number;
    fee_type: IRmaFeeType;
    taxable: boolean;
}

interface IRmaFeeType {
    label: string;
    value: FEE_TYPE;
}

@injectable()
class CreateRmaService {
	private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);
	
	public async fetchSettings(): Promise<ICreateRmaSettings> {
		const res = await this.httpService.get<IApiResponse<ICreateRmaSettings>>(`/rms-api/create-rma/fetch-settings`);
		return res.payload;
	}

	/**
	 * Loads Create RMA Internal defaults with FIXED monetary amounts already converted
	 * into the given order's payment currency.
	 */
	public async fetchDefaults(request: IFetchCreateRmaDefaultsRequest): Promise<ICreateRmaDefaultSettings> {
		const res = await this.httpService.post<IApiResponse<ICreateRmaDefaultSettings>>(
			`/rms-api/create-rma/fetch-defaults`,
			request,
		);
		return res.payload;
	}
 
	public async createRma(request: ICreateRmaDto): Promise<ICreateRMAResponse | IShouldRequestManagersApproval>{
		const res = await this.httpService.post<IApiResponse<ICreateRMAResponse>>(`/rms-api/create-rma`, request);
		return res.payload;
	}

    public async isRmaRequestOutOfPolicy(request: ICreateRmaDto): Promise<IOutOfPolicyResultDto> {
        const res = await this.httpService.post<IApiResponse<IOutOfPolicyResultDto>>(`/rms-api/is-out-of-policy`, request);
        return res.payload;
    }

    public async previewBalance(request: ICreateRmaDto): Promise<ICreateRmaBalancePreviewResultDto> {
        const res = await this.httpService.post<IApiResponse<ICreateRmaBalancePreviewResultDto>>(`/rms-api/create-rma/preview-balance`, request);
        return res.payload;
    }
    
    public async searchProductExchange(searchTerm: string, locationId?: string){
        const locationQuery = locationId ? `&locationId=${locationId}` : '';
		const res = await this.httpService.get<IApiResponse<IProductExchangeSearchResponse>>(`/rms-api/platform-products/product-search?term=${searchTerm}${locationQuery}`);
        return res.payload;
    }
    
    public async getVariants(productId: number, locationId?: string) {
        const locationQuery = locationId ? `?locationId=${locationId}` : '';
        const res = await this.httpService.get<IApiResponse<IPlatformProductVariant[]>>(`/rms-api/platform-products/${productId}/variants${locationQuery}`);
        return res.payload;
    }

    public async getCRmaItemFeeDataFromPolicyRule(request: ICRmaItemFeeDataRequest): Promise<ICRmaItemFeeDataResponse[]> {
        const res = await this.httpService.post<IApiResponse<ICRmaItemFeeDataResponse[]>>(`/rms-api/crma-item-fee-data-from-policy`, request);
        return res.payload;
    }

    public async getDynamicShippingOptions(request: ICreateRmaDto): Promise<IGetDynamicShippingOptionsResponse> {
        const res = await this.httpService.post<IApiResponse<IGetDynamicShippingOptionsResponse>>(`/rms-api/create-rma/fetch-shipping-rates`, request);
        return res.payload;
    }

}


export default CreateRmaService;
